import { ProgressBarStepProps } from '@ui-components/global/ComponentProps'
import { SPACE_LARGE_PLUS, SPACE_SMALL, SPACE_XXXXLARGE } from '@ui-components/global/spacing'
import { useEffect, useRef } from 'react'
import { styled } from 'styled-components'

import { SegmentedProgressBar } from '../../decorative/SegmentedProgressBar'
import { SecondaryHeaderWrapper } from '../../global/CommonStyling'
import {
  BREAKPOINT_DESKTOP_PX,
  BREAKPOINT_LARGE_DESKTOP_PX,
  DEFAULT_SCROLLED_OVER_HEIGHT_PX,
  STEP_HEADER_HEIGHT,
} from '../../global/constants'
import { useIsScrolledOver } from '../../hooks'
import { SecondaryTopBar, SecondaryTopBarProps } from '../../navigation/SecondaryTopBar'
import { shouldShowStaticTopBar } from './topBarHelpers'

export interface FloatingTopBarProps {
  secondaryTopBarProps?: SecondaryTopBarProps
  progressBarProps?: ProgressBarStepProps
}

export const FloatingBarWrapper = styled.div`
  padding: 0 ${SPACE_SMALL};

  @media (min-width: ${BREAKPOINT_DESKTOP_PX}px) {
    padding: 0 ${SPACE_LARGE_PLUS};
  }

  @media (min-width: ${BREAKPOINT_LARGE_DESKTOP_PX}px) {
    padding: 0 ${SPACE_XXXXLARGE};
  }
`

export const FloatingTopBar = ({ secondaryTopBarProps, progressBarProps }: FloatingTopBarProps) => {
  const showFloatingTopBar = useIsScrolledOver(DEFAULT_SCROLLED_OVER_HEIGHT_PX)
  const showStaticTopBar = shouldShowStaticTopBar({ secondaryTopBarProps, progressBarProps })
  const showProgressBar = progressBarProps && showFloatingTopBar
  const staticTopBarProps: Partial<SecondaryTopBarProps> = {
    ...secondaryTopBarProps,
    // Override default values in secondaryTopBarProps
    title: undefined,
    description: undefined,
    removeSidePadding: true,
    hideBoxShadow: true,
    hideButtonLabelsInMobileBreakpoint: false,
  }

  let topBarProps

  if (secondaryTopBarProps) {
    if (showFloatingTopBar) {
      topBarProps = secondaryTopBarProps
    } else if (showStaticTopBar) {
      topBarProps = staticTopBarProps
    }
  }

  const floatingBarRef = useRef<HTMLDivElement | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => {
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current)
    }
    timerRef.current = setTimeout(
      () => {
        floatingBarRef.current?.classList.add('visible')
      },
      process.env.NODE_ENV === 'test' ? 0 : 10,
    )
    return () => {
      clearTimeout(timerRef.current)
    }
  }, [showFloatingTopBar])

  return (
    topBarProps && (
      <>
        {showStaticTopBar && (
          <SecondaryHeader $visible={showFloatingTopBar} $fixedToTop={false} data-testid="secondary-header">
            {!showFloatingTopBar && <SecondaryTopBar {...topBarProps} small={false} titleTagName="h2" />}
          </SecondaryHeader>
        )}
        {showFloatingTopBar && (
          <FloatingSecondaryHeader ref={floatingBarRef} $fixedToTop={true}>
            <SecondaryTopBar {...secondaryTopBarProps} small={false} titleTagName="h2" />
            {showProgressBar && <SegmentedProgressBar {...progressBarProps} />}
          </FloatingSecondaryHeader>
        )}
      </>
    )
  )
}

const SecondaryHeader = styled(SecondaryHeaderWrapper)<{ $visible?: boolean }>`
  height: ${STEP_HEADER_HEIGHT}px;
  margin-bottom: 0;
  visibility: ${({ $visible }) => ($visible ? 'hidden' : 'visible')};
`

const FloatingSecondaryHeader = styled(SecondaryHeader)`
  transition: all 200ms;
  top: -${DEFAULT_SCROLLED_OVER_HEIGHT_PX}px;
  &.visible {
    top: 0;
  }
`
